import React, { useEffect, useState } from "react";
import { useSelector, shallowEqual, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

// Actions
import {
  SG_GET_TASK_SCHEDULES,
  SG_GET_TASK_TEMPLATES,
  SG_FETCH_TASK_SCHEDULE,
} from "constants/actions";

// Components
import PulseSurveySelector from "./PulseSurveySelector";

// Utils (copied from main component)
const getRecentResponses = (data, employees, filters, get_employee_categories) => {
  const responses = {};
  const employeeList = employees?.employees;

  if (!data?.task_responses || !Array.isArray(data.task_responses)) {
    return [];
  }

  const filteredEmployeeCategories = get_employee_categories?.filter((employeeCategory) => {
    if (!filters || filters.length === 0) {
      return true;
    }
    return filters.every(filter => {
      const matchingCategory = employeeCategory.category?.find(cat =>
        cat.value_id === filter.id
      );
      return !!matchingCategory;
    });
  })?.map((employeeCategory) => employeeCategory.employee_id) || [];

  data.task_responses.forEach((response) => {
    const employeeId = Number(response.employee);
    const employeeObj = employeeList?.find((emp) => emp.id === employeeId);
    response.employee_full_name = `${employeeObj?.first_name || ''} ${employeeObj?.last_name || ''}`;
    if (
      !responses[employeeId] ||
      response.created_at > responses[employeeId].created_at
    ) {
      responses[employeeId] = response;
    }
  });

  const filteredResponses = Object.values(responses).filter((response) => {
    if (!filters || filters.length === 0) {
      return true;
    }
    return filteredEmployeeCategories.includes(response.employee);
  });

  return filteredResponses;
};

const calculateCompliance = (participants, responses) => {
  if (!participants || participants.length === 0) {
    return 0;
  }

  const participantIds = participants.map(p => p.id);
  const responseEmployeeIds = responses.map(r => Number(r.employee));
  const respondedCount = participantIds.filter(id =>
    responseEmployeeIds.includes(id)
  ).length;
  const compliancePercentage = Math.round((respondedCount / participantIds.length) * 100);

  return {
    percentage: compliancePercentage,
    respondedCount,
    totalParticipants: participantIds.length
  };
};

const buildSurveyStructureData = (core_data) => {
  if (!core_data) {
    return {
      questionStructure: null,
      surveyStructure: null,
      standards: null,
      extractedQuestions: [],
      surveyQues: null
    };
  }

  const questionStructure = core_data?.questions || null;
  const surveyStructure = core_data?.categories?.[0] || null;
  const standards = core_data?.standards || null;
  
  let extractedQuestions = [];
  let surveyQues = null;

  if (questionStructure?.dimensions) {
    questionStructure.dimensions.forEach((dimension, dimIndex) => {
      if (dimension.subfactors) {
        dimension.subfactors.forEach((subfactor, subfactorIndex) => {
          if (subfactor.questions) {
            subfactor.questions.forEach((question, questionIndex) => {
              extractedQuestions.push({
                id: question.id || `${dimIndex}_${subfactorIndex}_${questionIndex}`,
                text: question.text || question.question || '',
                dimension: dimension.name || `Dimension ${dimIndex + 1}`,
                dimensionId: dimension.id || dimIndex,
                subfactor: subfactor.name || `Subfactor ${subfactorIndex + 1}`,
                subfactorId: subfactor.id || subfactorIndex,
                questionIndex: questionIndex,
                originalQuestion: question
              });
            });
          }
        });
      }
    });

    surveyQues = {
      questions: questionStructure,
      sort_order: questionStructure?.sort_order || null
    };
  }

  return {
    questionStructure,
    surveyStructure,
    standards,
    extractedQuestions,
    surveyQues
  };
};

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
  padding: 24px;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const PageTitle = styled.h1`
  font-size: 32px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0 0 8px 0;
`;

const PageSubtitle = styled.p`
  font-size: 16px;
  color: #6c757d;
  margin: 0;
`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SectionCard = styled.div`
  background: white;
  border-radius: 8px;
  padding: 24px;
  min-height: 400px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  transition: box-shadow 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
  margin: 0;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 4px;
  background-color: ${props => props.selected ? '#e8f5e8' : '#f5f5f5'};
  color: ${props => props.selected ? '#2d5016' : '#666'};
  font-size: 12px;
  font-weight: 500;
`;

const PulseSurveyStep = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [selectedPulseSurvey, setSelectedPulseSurvey] = useState(null);
  const [fetchingSurveyId, setFetchingSurveyId] = useState(null);

  // Redux selectors
  const {
    scheduledPulseSurveys,
    get_auth,
    pulseFetched,
    loading,
    fetched_scheduled_task,
    get_employees,
    filters,
    get_employee_categories,
    core_data
  } = useSelector(
    (state) => ({
      scheduledPulseSurveys: state.task_templates.scheduled_tasks,
      get_auth: state.auth,
      pulseFetched: state.task_templates.surveysFetched,
      loading: state.task_templates.loading,
      fetched_scheduled_task: state.task_templates?.fetched_scheduled_task,
      get_employees: state.employees,
      filters: state.audit?.new_filters,
      get_employee_categories: state.employee_category,
      core_data: state.audit?.core_data
    }),
    shallowEqual
  );

  // Load data on component mount
  useEffect(() => {
    dispatch({ type: SG_GET_TASK_TEMPLATES });
    dispatch({ type: SG_GET_TASK_SCHEDULES });
  }, [dispatch]);

  // Process the survey data when fetched_scheduled_task changes
  useEffect(() => {
    if (fetched_scheduled_task && get_employees?.employees && fetchingSurveyId) {
      try {
        const recent = getRecentResponses(
          fetched_scheduled_task, 
          get_employees, 
          filters || [], 
          get_employee_categories?.employee_org_category || []
        );

        const participants = Object?.values(
          fetched_scheduled_task?.user_requests?.user_requests || {}
        ) || [];

        const assignedEmployees = fetched_scheduled_task?.assigned_employees || [];
        const missingParticipants = assignedEmployees.filter(employee => 
          !participants.some(participant => participant.id === employee)
        );
        
        const missingParticipantsObjects = get_employees?.employees?.filter(employee => 
          missingParticipants.includes(employee.id)
        ) || [];
        
        missingParticipantsObjects.forEach(employee => {
          participants.push({ 
            id: employee.id, 
            email: employee.email || '', 
            email_sent: false, 
            first_name: employee.first_name || '', 
            status: "Pending" 
          });
        });

        const questions = fetched_scheduled_task?.task_template_user_tasks?.user_tasks?.filter(
          (task) => task.task_type === "question"
        ) || [];

        participants.forEach((participant) => {
          const employeeObj = get_employees?.employees?.find(
            (emp) => emp.id === participant.id
          );
          participant.employee_full_name = `${employeeObj?.first_name || ''} ${employeeObj?.last_name || ''}`;
        });

        const complianceData = calculateCompliance(participants, recent);
        const surveyStructureData = buildSurveyStructureData(core_data);

        const processedData = {
          responses: recent,
          participants,
          questions,
          compliance: complianceData,
          surveyInfo: {
            title: fetched_scheduled_task?.task_template_user_tasks?.name || fetched_scheduled_task?.name || "Unnamed Survey",
            description: fetched_scheduled_task?.description || "",
            createdBy: fetched_scheduled_task?.created_by?.name || "",
            createdDate: fetched_scheduled_task?.created_at || "",
            updatedDate: fetched_scheduled_task?.updated_at || "",
            surveyIsAnonymous: fetched_scheduled_task?.is_anonymous || false,
            surveyIsOpen: fetched_scheduled_task?.status !== 51,
            task_template_id: fetched_scheduled_task?.task_template_user_tasks?.id || null,
          },
          surveyStructure: {
            questionStructure: surveyStructureData.questionStructure,
            surveyStructure: surveyStructureData.surveyStructure,
            standards: surveyStructureData.standards,
            dimensions: surveyStructureData.questionStructure?.dimensions || [],
            categories: surveyStructureData.surveyStructure?.categories || [],
            surveyType: surveyStructureData.questionStructure?.type || null,
            useLikert: surveyStructureData.questionStructure?.useLikert || false,
            surveyQuestions: surveyStructureData.extractedQuestions || [],
            totalQuestions: surveyStructureData.extractedQuestions?.length || 0,
            completeSurveyQuestions: surveyStructureData.surveyQues || null
          },
          rawData: fetched_scheduled_task
        };

        // Navigate to step 2 with the pulse survey data
        navigate("/app/otp/otp-comparison/step2", {
          state: {
            pulseSurveyData: processedData,
            selectedPulseSurvey: selectedPulseSurvey
          }
        });

      } catch (error) {
        console.error("Error processing survey data:", error);
        setFetchingSurveyId(null);
      }
    }
  }, [fetched_scheduled_task, get_employees, fetchingSurveyId, filters, get_employee_categories, core_data, navigate, selectedPulseSurvey]);

  const handlePulseSurveySelect = (survey) => {
    setSelectedPulseSurvey(survey);
    setFetchingSurveyId(survey.id);
    
    // Fetch the detailed survey data
    dispatch({ type: SG_FETCH_TASK_SCHEDULE, payload: survey.id });
  };

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Data Comparison Tool</PageTitle>
        <PageSubtitle>Step 1: Select a pulse survey for analysis</PageSubtitle>
      </PageHeader>

      <Container>
        <SectionCard>
          <SectionHeader>
            <SectionTitle>Select Pulse Survey</SectionTitle>
            <StatusIndicator selected={!!selectedPulseSurvey}>
              {selectedPulseSurvey ? '✓ Selected' : 'Not Selected'}
            </StatusIndicator>
          </SectionHeader>
          
          <PulseSurveySelector
            scheduledSurveys={scheduledPulseSurveys}
            onSelect={handlePulseSurveySelect}
            selectedSurvey={selectedPulseSurvey}
            loading={loading}
            fetched={pulseFetched}
            fetchingSurveyId={fetchingSurveyId}
          />
        </SectionCard>
      </Container>
    </PageContainer>
  );
};

export default PulseSurveyStep; 